import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { PDFViewer } from '@odo/shared-ui';
import api from '../../../../api/config';
import { getDownloadUrl } from '../../../../services/cloud-files';

// Shared shape for a versioned clinical document (Dx report / SDI / RDI).
// DxReportVersion and SdiVersion are structurally identical to this.
export interface DocumentVersion {
    id: string;
    version: number;
    createdAt: string;
    patientFileId: string;
    fileName: string;
}

interface VersionedPdfPaneProps {
    patientId: string | null;
    /** Latest-first version list; Monday imports appear too, as separate v0 entries, not collapsed. */
    versions: DocumentVersion[];
    /** Label for empty/loading copy (e.g. "Dx report", "SDI", "RDI"). */
    docLabel?: string;
}

interface VersionDropdownProps {
    versions: DocumentVersion[];
    selectedId: string | null;
    onSelect: (id: string) => void;
}

function VersionDropdown({ versions, selectedId, onSelect }: VersionDropdownProps) {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [open]);

    const current = versions.find((v) => v.id === selectedId) ?? versions[0];

    return (
        <div ref={wrapperRef} className="relative min-w-0 max-w-[320px]">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={open}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors text-sm font-medium text-gray-700 min-w-0 max-w-full"
            >
                <span className="text-[10px] font-semibold text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">
                    v{current.version}
                </span>
                <span className="min-w-0 flex-1 truncate">{current.fileName}</span>
                <ChevronDownIcon
                    className={
                        'w-4 h-4 flex-shrink-0 text-gray-400 transition-transform ' +
                        (open ? 'rotate-180' : '')
                    }
                />
            </button>

            {open && (
                <div
                    role="menu"
                    className="absolute left-0 top-full mt-1 z-30 w-[360px] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
                >
                    {versions.map((v) => {
                        const isActive = v.id === current.id;
                        return (
                            <button
                                key={v.id}
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                    setOpen(false);
                                    onSelect(v.id);
                                }}
                                className={
                                    'w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors border-b border-gray-100 last:border-b-0 ' +
                                    (isActive ? 'bg-gray-50' : 'hover:bg-gray-50')
                                }
                            >
                                <span className="text-[10px] font-semibold text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">
                                    v{v.version}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-800 truncate">
                                        {v.fileName}
                                    </div>
                                    <div className="text-[11px] text-gray-500">
                                        {v.createdAt
                                            ? new Date(v.createdAt).toLocaleDateString('en-US', {
                                                  month: 'short',
                                                  day: 'numeric',
                                                  year: 'numeric'
                                              })
                                            : ''}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// Each version resolves its PDF through cloud-files, so a work-queue-created document shows immediately.
export function VersionedPdfPane({
    patientId,
    versions,
    docLabel = 'document'
}: VersionedPdfPaneProps) {
    const displayVersions = versions;
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const userPickedRef = useRef(false);

    // A different patient is a fresh document set — drop any manual choice.
    useEffect(() => {
        userPickedRef.current = false;
    }, [patientId]);

    useEffect(() => {
        if (displayVersions.length === 0) {
            setSelectedId(null);
            return;
        }
        setSelectedId((prev) => {
            // Preserve an explicit user selection as long as it's still available.
            if (userPickedRef.current && prev && displayVersions.some((v) => v.id === prev)) {
                return prev;
            }
            // Otherwise default to the latest version (head of the latest-first list).
            return displayVersions[0].id;
        });
    }, [displayVersions]);

    const handleSelectVersion = (id: string): void => {
        userPickedRef.current = true;
        setSelectedId(id);
    };

    const selectedVersion = displayVersions.find((v) => v.id === selectedId) ?? null;

    const [versionUrl, setVersionUrl] = useState<string | null>(null);
    const [versionError, setVersionError] = useState<string | null>(null);
    useEffect(() => {
        if (!selectedVersion || !patientId) {
            setVersionUrl(null);
            setVersionError(null);
            return;
        }
        let cancelled = false;
        setVersionUrl(null);
        setVersionError(null);
        getDownloadUrl(patientId, selectedVersion.patientFileId)
            .then((url) => {
                if (!cancelled) setVersionUrl(url);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                setVersionError(
                    err instanceof Error ? err.message : 'Failed to load this version.'
                );
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVersion?.patientFileId, patientId]);

    const resolvedPdfUrl = versionUrl;

    if (!resolvedPdfUrl && !versionError) {
        return (
            <div className="h-full flex flex-col items-center justify-center gap-3 p-8 text-center bg-white border border-gray-200 rounded-xl">
                <DocumentTextIcon className="w-8 h-8 text-gray-300" />
                <div className="text-sm text-gray-500">
                    {displayVersions.length > 0
                        ? `Loading ${docLabel}...`
                        : `No ${docLabel} attached to this case yet.`}
                </div>
            </div>
        );
    }

    if (versionError) {
        return (
            <div className="h-full flex flex-col items-center justify-center gap-3 p-8 text-center bg-white border border-gray-200 rounded-xl">
                <DocumentTextIcon className="w-8 h-8 text-red-300" />
                <div className="text-sm text-red-600">{versionError}</div>
            </div>
        );
    }

    const leadingControls = (() => {
        if (displayVersions.length <= 1) {
            if (!selectedVersion) return null;
            return (
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 min-w-0 max-w-[320px]">
                    <span className="text-[10px] font-semibold text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">
                        v{selectedVersion.version}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{selectedVersion.fileName}</span>
                </div>
            );
        }
        return (
            <VersionDropdown
                versions={displayVersions}
                selectedId={selectedId}
                onSelect={handleSelectVersion}
            />
        );
    })();

    return (
        <div className="h-full bg-white border border-gray-200 rounded-xl overflow-hidden p-3 scrollbar-hide-deep">
            <PDFViewer
                pdfUrl={resolvedPdfUrl!}
                apiBaseUrl={api.defaults.baseURL ?? ''}
                showControls
                className="h-full"
                getAuthToken={() => localStorage.getItem('odo_login_token')}
                leadingControls={leadingControls}
            />
        </div>
    );
}
